import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { unitId, actionId } from '@/annotations'
import { AuthCard } from '@/components/AuthCard'
import { AuthCardBrand } from '@/components/AuthCardBrand'
import { AuthCardLead } from '@/components/AuthCardLead'
import { FieldStack } from '@/components/FieldStack'
import { Field } from '@/components/Field'
import { Button } from '@/components/Button'
import { Note } from '@/components/Note'
import { Chip } from '@/components/Chip'
import { OrDivider } from '@/components/OrDivider'
import { getApiClient, ApiProblemError, WafChallengeError, type Problem } from '@/data/apiClient'
import { setAccessToken } from '@/data/store'
import { AUTH_BRAND, A_SIGNIN_COPY } from '@/copy/registry'
import { TEXT_INPUT_CLASSNAME, TEXT_INPUT_STYLE } from '@/styles/formControls'
import { FormError, RateLimitedNotice, UnexpectedRefusal, WafChallengeNotice, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'
import type { SignInBanner } from '@/layers/auth/state'

/**
 * `a-signin` — the ordinary entry point. `identitySignIn`'s result has three real shapes
 * (SignInResult = SessionPair | ChallengeContinuation) plus two refusals that must never render
 * as "wrong password" (§3.1i, `LLD-IDN-043`).
 *
 * **Three real fixes found and closed W173, none of them caught by any prior pass because
 * `a-signin`'s own spec Components table names neither `chip` nor `or-divider` at all** (nor does
 * `_family-auth.md`'s shared baseline) — a real gap between what the spec requires and what the
 * prototype's own markup for this exact unit draws, reported here rather than left standing now
 * that it is found. (1) The SSO button was missing `ghost` entirely, rendering as a disabled
 * *solid* button (`bg-slate`) crammed directly against `Continue` with zero space between them —
 * the prototype's own class is `btn ghost full`. (2) `or-divider` (`div.orline`, catalogued at
 * `process/08-COMPONENT-CATALOGUE.md`) never existed anywhere in this build; built now and placed
 * between the two sign-in methods, matching the prototype exactly. (3) The "Planned" chip was
 * `off` (`st-off`, grey); the prototype's own element is `st-pending` (light blue) — `pending`.
 *
 * **`onGoToSignUp` / the "Create one" link added.** Neither the prototype's own `a-signin` markup
 * nor its spec draws a sign-up link — but `a-signup` is a real, fully-built screen (`identitySignUp`
 * takes an organisation name directly: self-serve tenant creation, not merely an invite-acceptance
 * form) that had, until now, no entry point anywhere in the app at all. Added the conventional
 * reverse of `GO_TO_SIGNIN`'s own link, rather than leaving a fully-built screen permanently dead.
 */
export function ASignin({
  banner,
  onSignedIn,
  onGoToForgot,
  onGoToSignUp,
  onAccountUnconfirmed,
  onPasswordResetRequired,
}: {
  banner?: SignInBanner
  onSignedIn: (email: string, challenge?: { kind: 'SOFTWARE_TOKEN_MFA' | 'MFA_SETUP'; continuation: string }) => void
  onGoToForgot: () => void
  onGoToSignUp: () => void
  onAccountUnconfirmed: (email: string) => void
  onPasswordResetRequired: () => void
}) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const signIn = useMutation({
    mutationFn: async () => {
      const client = getApiClient()
      const { data, error, response } = await client.POST('/auth/sign-in', { body: { email, password } })
      if (error) {
        if (response.status === 405) throw new WafChallengeError()
        throw new ApiProblemError(error as Problem)
      }
      return data
    },
    onSuccess: (data) => {
      if (!data) return
      if ('challenge' in data && data.challenge) {
        onSignedIn(email, { kind: data.challenge, continuation: data.continuation })
      } else if ('access_token' in data) {
        setAccessToken(data.access_token)
        onSignedIn(email)
      }
    },
    onError: (err) => {
      if (!(err instanceof ApiProblemError)) return
      if (err.problem.refusal_kind === 'ACCOUNT_UNCONFIRMED') onAccountUnconfirmed(email)
      if (err.problem.refusal_kind === 'PASSWORD_RESET_REQUIRED') onPasswordResetRequired()
    },
  })

  const problem = signIn.error instanceof ApiProblemError ? signIn.error.problem : undefined
  const wafChallenge = signIn.error instanceof WafChallengeError

  return (
    <AuthCard unitId={unitId('a-signin')}>
      <AuthCardBrand label={AUTH_BRAND} />
      <h3 className="m-0 mb-3 text-section font-semibold">{A_SIGNIN_COPY.heading}</h3>
      <AuthCardLead>{A_SIGNIN_COPY.lead}</AuthCardLead>

      {banner === 'password-changed' && <Note>{A_SIGNIN_COPY.passwordChangedBanner}</Note>}
      {banner === 'signed-out-everywhere' && <Note>{A_SIGNIN_COPY.signedOutEverywhereBanner}</Note>}

      <FieldStack>
        <Field label={A_SIGNIN_COPY.emailLabel}>
          <input type="email" className={TEXT_INPUT_CLASSNAME} style={TEXT_INPUT_STYLE} value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label={A_SIGNIN_COPY.passwordLabel}>
          <input type="password" className={TEXT_INPUT_CLASSNAME} style={TEXT_INPUT_STYLE} value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
      </FieldStack>

      <p className="text-right mb-9">
        <a href="#" data-act={actionId('nav.a-forgot')} onClick={(e) => { e.preventDefault(); onGoToForgot() }} className="text-link text-small">
          {A_SIGNIN_COPY.forgotLinkLabel}
        </a>
      </p>

      {wafChallenge && <WafChallengeNotice />}
      {problem && <ASigninRefusal problem={problem} />}

      <Button data-act={actionId('session.signIn')} disabled={signIn.isPending || !email || !password} onClick={() => signIn.mutate()}>
        {signIn.isPending ? 'Signing in…' : A_SIGNIN_COPY.submitButton}
      </Button>

      <OrDivider label={A_SIGNIN_COPY.ssoOrLabel} />

      <Button data-act={undefined} ghost disabled aria-disabled="true" title="Single sign-on is not available yet">
        {A_SIGNIN_COPY.ssoButton} <Chip variant="pending">{A_SIGNIN_COPY.ssoPlannedChip}</Chip>
      </Button>
      <Note>{A_SIGNIN_COPY.ssoNote}</Note>

      <p className="text-center mt-7">
        <a href="#" data-act={actionId('nav.a-signup')} onClick={(e) => { e.preventDefault(); onGoToSignUp() }} className="text-link text-small">
          {A_SIGNIN_COPY.signUpLinkLabel}
        </a>
      </p>
    </AuthCard>
  )
}

function ASigninRefusal({ problem }: { problem: Problem }) {
  switch (problem.refusal_kind) {
    case 'CREDENTIAL_REJECTED':
    case 'ACCOUNT_DEACTIVATED':
      return <FormError>Sign-in was refused. Check your email and password and try again.</FormError>
    case 'VALIDATION_FAILED':
      return <FormError>{problem.errors?.map((e) => e.message).join(' ') ?? problem.title}</FormError>
    case 'RATE_LIMITED':
      return <RateLimitedNotice problem={problem} />
    default:
      return isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <FormError>{problem.title}</FormError>
  }
}
